/**
 * The second data channel: unreliable, unordered, and the one snapshots ride.
 *
 * Trystero opens one channel per peer and it is reliable and ordered, which is right for a lobby
 * event and wrong for a snapshot. A snapshot is worthless the moment a newer one exists, so
 * retransmitting a lost one costs latency to deliver something already stale, and ordering makes
 * one dropped packet hold up every packet behind it.
 *
 * WebRTC normally needs a renegotiation to add a channel, and Trystero does not expose one. The way
 * around it is a pre-negotiated channel: both peers create the same channel id without telling each
 * other, and the moment SCTP is up it works. No offer, no answer, and no creator-versus-listener
 * asymmetry to get wrong, which matters because getting that wrong on one side is a channel that
 * silently never opens.
 *
 * The id is a coupling to a library internal: Trystero's own in-band channel takes stream 0 or 1
 * depending on the DTLS role, so this sits well clear of it. If a future version opens more
 * channels the failure is loud, and it lands in the fallback below rather than in corrupt data.
 *
 * Everything that can go wrong ends in the same place: no unreliable channel, snapshots go out on
 * the reliable one, and both surfaces say so. Degraded and visible beats broken and quiet.
 */

const SNAPSHOT_CHANNEL_LABEL = 'snapshot';

/** Clear of the stream ids Trystero's own channel can take. */
const SNAPSHOT_CHANNEL_ID = 8;

/** SCTP is up when a peer is announced, so a channel not open by now never will be. */
const OPEN_TIMEOUT_MS = 3000;

export interface SnapshotChannelHandlers {
  /** The channel is open and carrying. */
  onReady(): void;
  /** No unreliable channel for this peer. Snapshots fall back to the reliable one. */
  onUnavailable(reason: string): void;
  onMessage(bytes: ArrayBuffer): void;
}

export interface SnapshotChannel {
  /** False means the fallback is in force and `send` will refuse. */
  isReady(): boolean;
  /** True when the bytes went out unreliably. False means the caller must send them reliably. */
  send(bytes: Uint8Array<ArrayBuffer>): boolean;
  close(): void;
}

/** Opens the pre-negotiated channel on a peer connection Trystero has already established. */
export function openSnapshotChannel(
  connection: RTCPeerConnection,
  handlers: SnapshotChannelHandlers,
): SnapshotChannel {
  let ready = false;
  let closed = false;
  let channel: RTCDataChannel;

  const giveUp = (reason: string): void => {
    if (closed) return;
    ready = false;
    handlers.onUnavailable(reason);
  };

  try {
    channel = connection.createDataChannel(SNAPSHOT_CHANNEL_LABEL, {
      negotiated: true,
      id: SNAPSHOT_CHANNEL_ID,
      ordered: false,
      maxRetransmits: 0,
    });
  } catch (cause) {
    // A browser that refuses the id, or a version of Trystero that already took it. One peer's
    // snapshots get slower, and the session still works.
    const detail = cause instanceof Error ? cause.message : String(cause);
    const unavailable: SnapshotChannel = {
      isReady: (): boolean => false,
      send: (): boolean => false,
      close: (): void => undefined,
    };
    handlers.onUnavailable(detail);
    return unavailable;
  }

  channel.binaryType = 'arraybuffer';

  const timer = setTimeout(() => {
    if (!ready) giveUp('the unreliable channel never opened');
  }, OPEN_TIMEOUT_MS);

  channel.addEventListener('open', () => {
    ready = true;
    clearTimeout(timer);
    handlers.onReady();
  });

  channel.addEventListener('message', (event: MessageEvent<ArrayBuffer>) => {
    handlers.onMessage(event.data);
  });

  channel.addEventListener('error', () => {
    clearTimeout(timer);
    giveUp('the unreliable channel errored');
  });

  channel.addEventListener('close', () => {
    clearTimeout(timer);
    giveUp('the unreliable channel closed');
  });

  return {
    isReady: (): boolean => ready,

    send(bytes: Uint8Array<ArrayBuffer>): boolean {
      if (!ready || channel.readyState !== 'open') return false;
      try {
        channel.send(bytes);
        return true;
      } catch {
        // A full send buffer or a channel closing under us. The caller resends reliably.
        giveUp('the unreliable channel refused a send');
        return false;
      }
    },

    close(): void {
      closed = true;
      ready = false;
      clearTimeout(timer);
      channel.close();
    },
  };
}
